import {reportBug} from '@packages/utilities/reporter';
import computeLayoutMatrix from './computeLayoutMatrix';
import {NEW_CELL_PREFIX, AXIS_ACTIONS, LAYOUT_AXIS} from '../constants';
import type {
  LayoutLocations,
  LayoutMatrix,
  LayoutAxis,
  LayoutAxisAction,
  LayoutGridTemplate,
  CellId,
} from '../types';
import {deepCopyMatrix, getSafeRowLength, getTemplateList} from '../utils';
import type {BreakpointID} from '@packages/systems/style/types';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import isRowEditingLocked from './isRowEditingLocked';

type AxisActionOptions = {
  index: number;
  count?: number;
  removeUnusedRows?: boolean;
  targetMatrix?: LayoutMatrix;
};

/**
 * Computes the target Layout matrix and grid template
 * when adding or removing columns and rows.
 */
const computeUpdateAxis = (
  locations: LayoutLocations,
  layoutGridTemplate: LayoutGridTemplate,
  axis: LayoutAxis,
  action: LayoutAxisAction,
  mediaQuery: BreakpointID,
  variantState: VariantStateKey | null,
  /**
   * This index enables adding/removing rows & columns at any position in the
   * Layout. If the index is missing, it'll default to adding/removing at the end.
   */
  index?: number,
  /** Used for deleting multiple rows/columns in one action */
  count?: number
): {
  targetMatrix: LayoutMatrix;
  targetGridTemplate: LayoutGridTemplate;
} => {
  const matrix = computeLayoutMatrix(locations);
  // On non-main breakpoints we don't allow row changes. We also don't allow
  // updating the matrix because the total number of Cells should not change
  const lockMatrix = isRowEditingLocked(mediaQuery, variantState);
  const disableRowChanges = lockMatrix;

  switch (action) {
    case AXIS_ACTIONS.ADD:
      return addAxis({
        matrix,
        axis,
        layoutGridTemplate,
        index,
        lockMatrix,
        disableRowChanges,
      });
    case AXIS_ACTIONS.REMOVE:
      return removeAxis({
        matrix,
        axis,
        layoutGridTemplate,
        index,
        lockMatrix,
        disableRowChanges,
        count,
      });
    default: {
      reportError(`Unknown layout axis action ${action}`);
      return {targetMatrix: matrix, targetGridTemplate: layoutGridTemplate};
    }
  }
};

type AxisChangeProps = {
  matrix: LayoutMatrix;
  axis: LayoutAxis;
  layoutGridTemplate: LayoutGridTemplate;
  index?: number;
  lockMatrix: boolean;
  disableRowChanges: boolean;
  count?: number;
};
/**
 * Adds a row or a column to the matrix and updates the layout grid template
 */
function addAxis({
  matrix,
  axis,
  layoutGridTemplate,
  index,
  lockMatrix,
  disableRowChanges,
}: AxisChangeProps): {
  targetMatrix: LayoutMatrix;
  targetGridTemplate: LayoutGridTemplate;
} {
  switch (axis.key) {
    case LAYOUT_AXIS.rows.key: {
      if (disableRowChanges) {
        return {
          targetMatrix: matrix,
          targetGridTemplate: layoutGridTemplate,
        };
      }
      // The index determines at what location we're adding the row.
      // If no index is passed, we default to adding it to the end.
      const addRowIndex = index ?? matrix.length;
      return {
        targetMatrix: addRowInMatrix(matrix, {
          index: addRowIndex,
          removeUnusedRows: lockMatrix,
        }),
        targetGridTemplate: addAxisInGridTemplate(layoutGridTemplate, axis, {
          index: addRowIndex,
        }),
      };
    }
    case LAYOUT_AXIS.columns.key: {
      // The index determines at what location we're adding the column.
      // If no index is passed, we default to adding it to the end.
      const addColumnIndex = index ?? getSafeRowLength(matrix);
      const targetMatrix = lockMatrix
        ? matrix
        : addColumnInMatrix(matrix, {
            index: addColumnIndex,
          });
      const updatedGridTemplate = addAxisInGridTemplate(
        layoutGridTemplate,
        axis,
        {
          index: addColumnIndex,
          removeUnusedRows: lockMatrix,
          targetMatrix,
        }
      );

      return {
        targetMatrix,
        targetGridTemplate: updatedGridTemplate,
      };
    }
    default: {
      reportError(`Unknown layout axis ${axis.key}`);
      return {targetMatrix: matrix, targetGridTemplate: layoutGridTemplate};
    }
  }
}

/**
 * Removes a row or a column from the matrix and updates the layout grid template
 */
function removeAxis({
  matrix,
  axis,
  layoutGridTemplate,
  index,
  lockMatrix,
  disableRowChanges,
  count,
}: AxisChangeProps): {
  targetMatrix: LayoutMatrix;
  targetGridTemplate: LayoutGridTemplate;
} {
  switch (axis.key) {
    case LAYOUT_AXIS.rows.key: {
      if (disableRowChanges) {
        return {
          targetMatrix: matrix,
          targetGridTemplate: layoutGridTemplate,
        };
      }
      // The index determines at what location we're removing the row.
      // If no index is passed, we default to removing it from the end.
      const removeRowIndex = index ?? matrix.length - 1;
      return {
        targetMatrix: removeRowInMatrix(matrix, {index: removeRowIndex, count}),
        targetGridTemplate: removeAxisInGridTemplate(layoutGridTemplate, axis, {
          index: removeRowIndex,
          count,
        }),
      };
    }
    case LAYOUT_AXIS.columns.key: {
      // The index determines at what location we're removing the column.
      // If no index is passed, we default to removing it from the end.
      const length = getSafeRowLength(matrix, 1);
      const removeColumnIndex = index ?? length - 1;
      const updatedTargetMatrix = lockMatrix
        ? matrix
        : removeColumnInMatrix(matrix, {
            index: removeColumnIndex,
            count,
          });
      const updatedGridTemplate = removeAxisInGridTemplate(
        layoutGridTemplate,
        axis,
        {
          index: removeColumnIndex,
          count,
          removeUnusedRows: lockMatrix,
          targetMatrix: updatedTargetMatrix,
        }
      );

      return {
        targetMatrix: updatedTargetMatrix,
        targetGridTemplate: updatedGridTemplate,
      };
    }
    default: {
      reportError(`Unknown layout axis ${axis.key}`);
      return {targetMatrix: matrix, targetGridTemplate: layoutGridTemplate};
    }
  }
}

/**
 * Add row to matrix at a specific index.
 * Exported for tests only
 */
export function addRowInMatrix(
  matrix: LayoutMatrix,
  {index: rowIndex}: AxisActionOptions
): LayoutMatrix {
  if (rowIndex < 0 || rowIndex > matrix.length) {
    reportError(`Add row index is out of range: ${rowIndex}`);
    return matrix;
  }
  const newRow: Array<CellId> = [];

  const columns = getSafeRowLength(matrix);
  for (let colIndex = 0; colIndex < columns; colIndex++) {
    // handles merge cells
    const topCellFromColIndex =
      matrix[rowIndex - 1]?.[colIndex] ?? 'undefined-top-cell';
    const bottomCellFromColIndex =
      matrix[rowIndex]?.[colIndex] ?? 'undefined-bottom-cell';
    const areBothCellsMerged = topCellFromColIndex === bottomCellFromColIndex;

    areBothCellsMerged
      ? newRow.push(topCellFromColIndex)
      : newRow.push(`${NEW_CELL_PREFIX}_${rowIndex}_${colIndex}`);
  }

  const targetMatrix = deepCopyMatrix(matrix);
  const updatedMatrix = [
    ...targetMatrix.slice(0, rowIndex), // rows before index
    newRow, // new row
    ...targetMatrix.slice(rowIndex, targetMatrix.length), // row after index
  ];
  return updatedMatrix;
}

/**
 * Add column to matrix at a specific index.
 * Exported for tests only
 */
export function addColumnInMatrix(
  matrix: LayoutMatrix,
  {index: columnIndex}: AxisActionOptions
): LayoutMatrix {
  const maxColumns = getSafeRowLength(matrix);
  if (columnIndex < 0 || columnIndex > maxColumns) {
    reportError(`Add column index is out of range: ${columnIndex}`);
    return matrix;
  }

  const rows = matrix.length;
  const targetMatrix = deepCopyMatrix(matrix);
  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    const row = targetMatrix[rowIndex];

    // handles merge cells
    const leftCellFromRowIndex =
      row?.[columnIndex - 1] ?? 'undefined-left-cell';
    const rightCellFromRowIndex = row?.[columnIndex] ?? 'undefined-right-cell';
    const areBothCellsMerged = leftCellFromRowIndex === rightCellFromRowIndex;

    const updatedRow = [
      // @ts-expect-error - TS18048 - 'row' is possibly 'undefined'.
      ...row.slice(0, columnIndex), // cells before index
      // If the left and right cells match we default to leftCellFromRowIndex id.
      areBothCellsMerged
        ? leftCellFromRowIndex
        : `${NEW_CELL_PREFIX}_${rowIndex}_${columnIndex}`, // new cell
      // @ts-expect-error - TS18048 - 'row' is possibly 'undefined'. | TS18048 - 'row' is possibly 'undefined'.
      ...row.slice(columnIndex, row.length), // cells after index
    ];
    targetMatrix[rowIndex] = updatedRow;
  }

  return targetMatrix;
}

/**
 * Removes a row from the matrix at a specific index.
 * Exported for tests only
 */
export function removeRowInMatrix(
  matrix: LayoutMatrix,
  {index: rowIndex, count}: AxisActionOptions
): LayoutMatrix {
  /**
   * The index of the last row being removed, which will differ from `rowIndex` for merged cells.
   * The row `count` is one-based (cannot delete fewer than 1 row),
   * so we do some math to convert it to a zero-based index.
   */
  const endRowIndex = rowIndex + (count ?? 1) - 1;

  if (rowIndex < 0 || endRowIndex >= matrix.length) {
    reportError(`Remove row index is out of range: ${rowIndex}`);
    return matrix;
  }

  const targetMatrix = deepCopyMatrix(matrix);
  return [
    ...targetMatrix.slice(0, rowIndex), // rows before index
    ...targetMatrix.slice(endRowIndex + 1, targetMatrix.length), // rows after end index
  ];
}

/**
 * Removes a column from the matrix at a specific index.
 * Exported for tests only
 */
export function removeColumnInMatrix(
  matrix: LayoutMatrix,
  {index: columnIndex, count}: AxisActionOptions
): LayoutMatrix {
  /**
   * The index of the last column being removed, which will differ from `columnIndex` for merged cells.
   * The column `count` is one-based (cannot delete fewer than 1 column),
   * so we do some math to convert it to a zero-based index.
   */
  const endColumnIndex = columnIndex + (count ?? 1) - 1;

  const length = getSafeRowLength(matrix, 1);
  const maxColumns = length - 1;
  if (columnIndex < 0 || endColumnIndex > maxColumns) {
    reportError(`Remove column index is out of range: ${columnIndex}`);
    return matrix;
  }

  const rows = matrix.length;
  const targetMatrix = deepCopyMatrix(matrix);
  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    const row = targetMatrix[rowIndex];
    const updatedRow = [
      // @ts-expect-error - TS18048 - 'row' is possibly 'undefined'.
      ...row.slice(0, columnIndex), // cells before index
      // @ts-expect-error - TS18048 - 'row' is possibly 'undefined'. | TS18048 - 'row' is possibly 'undefined'.
      ...row.slice(endColumnIndex + 1, row.length), // cells after end index
    ];
    targetMatrix[rowIndex] = updatedRow;
  }

  return targetMatrix;
}

/**
 * Updates the layout grid template by adding a row or column.
 * Exported for tests only
 *
 * Layout grid template example:
 * {
 *  gridTemplateRows: 'minmax(75px, auto) minmax(75px, auto)',
 *  gridTemplateColumns: 'minmax(75px, 1fr) minmax(75px, 1fr)',
 * }
 */
export function addAxisInGridTemplate(
  layoutGridTemplate: LayoutGridTemplate,
  axis: LayoutAxis,
  {index, removeUnusedRows, targetMatrix}: AxisActionOptions
): LayoutGridTemplate {
  const gridTemplateList = getTemplateList(layoutGridTemplate[axis.property]);
  if (index < 0 || index > gridTemplateList.length) {
    reportError(
      `Update template index is out of range: ${index}. Template for ${
        axis.key
      } was "${layoutGridTemplate[axis.property]}".`
    );
    return layoutGridTemplate;
  }

  const newAxisValue = [
    ...gridTemplateList.slice(0, index), // items before index
    axis.value, // new item
    ...gridTemplateList.slice(index, gridTemplateList.length), // items after index
  ].join(' ');

  let updatedGridTemplate = {
    ...layoutGridTemplate,
    [axis.property as string]: newAxisValue,
  };

  // remove any empty rows, when not on main breakpoint
  if (removeUnusedRows && targetMatrix) {
    updatedGridTemplate = removeEmptyTemplateRows(
      targetMatrix,
      updatedGridTemplate
    );
  }

  return updatedGridTemplate;
}

/**
 * Updates the layout grid template by removing a row or column.
 * Exported for tests only
 *
 * Layout grid template example:
 * {
 *  gridTemplateRows: 'minmax(75px, auto) minmax(75px, auto)',
 *  gridTemplateColumns: 'minmax(75px, 1fr) minmax(75px, 1fr)',
 * }
 */
export function removeAxisInGridTemplate(
  layoutGridTemplate: LayoutGridTemplate,
  axis: LayoutAxis,
  {index, removeUnusedRows, targetMatrix, count}: AxisActionOptions
): LayoutGridTemplate {
  const gridTemplateList = getTemplateList(layoutGridTemplate[axis.property]);
  const endIndex = index + (count ?? 1) - 1;
  if (index < 0 || endIndex > gridTemplateList.length) {
    reportError(
      `Update template index is out of range: ${index}. Template for ${
        axis.key
      } was "${layoutGridTemplate[axis.property]}".`
    );
    return layoutGridTemplate;
  }

  const newAxisValue = [
    ...gridTemplateList.slice(0, index), // items before index
    ...gridTemplateList.slice(endIndex + 1, gridTemplateList.length), // items after end index
  ].join(' ');

  let updatedGridTemplate = {
    ...layoutGridTemplate,
    // Needs to be casted to a string because Flow doesn't allow union types to
    // be used as computed properties.
    [axis.property as string]: newAxisValue,
  };

  // remove any empty rows, when not on main breakpoint
  if (removeUnusedRows && targetMatrix) {
    updatedGridTemplate = removeEmptyTemplateRows(
      targetMatrix,
      updatedGridTemplate
    );
  }

  return updatedGridTemplate;
}

/**
 * On Non-desktop breakpoints we add columns, but do not add cells. This removes unfilled rows for these instances.
 */
export function removeEmptyTemplateRows(
  matrix: LayoutMatrix,
  layoutGridTemplate: LayoutGridTemplate
): LayoutGridTemplate {
  const cellCount = matrix?.flat()?.length || 0;

  if (cellCount <= 0) {
    reportError('There are no cells in Matrix');
    return layoutGridTemplate;
  }

  const splitColumnValues = getTemplateList(
    layoutGridTemplate.gridTemplateColumns
  );
  const splitRowValues = getTemplateList(layoutGridTemplate.gridTemplateRows);
  const totalCellSlots = splitColumnValues.length * splitRowValues.length;
  const unUsedCellSlots = totalCellSlots - cellCount;
  // divides empty cell slots by number of columns, to find any full empty rows
  const unUsedRows = Math.floor(unUsedCellSlots / splitColumnValues.length);
  // remove unused rows from gridTemplateRows
  const newRowValues =
    unUsedRows > 0 ? splitRowValues.slice(0, -unUsedRows) : splitRowValues;

  return {
    ...layoutGridTemplate,
    gridTemplateRows: newRowValues.join(' '),
  };
}

function reportError(reason: string) {
  reportBug('LayoutError', reason, {
    team: 'structure',
    ticket: 'https://webflow.atlassian.net/browse/ACT-1628',
    method: 'computeUpdateAxis',
  });
}

export default computeUpdateAxis;
